import React, { Component, useEffect, useState } from 'react'

// EPIC-63: the public onboarding page gets its own tree so it never loads Supabase. The router
// imports useAuth (and through it the Supabase client), so it stays behind this lazy boundary.
const AuthenticatedApp = React.lazy(() => import('./authenticated-app'))
const PublicOnboardingApp = React.lazy(() => import('./public-onboarding-app'))

export const isPublicOnboardingHash = (hash: string) => /^#\/onboard\/[^/?#]+\/?$/.test(hash)

class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Unhandled error:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <img src="/fanfair.png" alt="FanFair" className="h-8 mx-auto" />
            <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
            <p className="text-muted-foreground">An unexpected error occurred. Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  const [isPublic, setIsPublic] = useState(() => isPublicOnboardingHash(window.location.hash))
  useEffect(() => {
    const onHashChange = () => setIsPublic(isPublicOnboardingHash(window.location.hash))
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])
  return (
    <ErrorBoundary>
      <React.Suspense fallback={<div data-testid="loading-spinner">Loading...</div>}>
        {isPublic ? <PublicOnboardingApp /> : <AuthenticatedApp />}
      </React.Suspense>
    </ErrorBoundary>
  )
}
